import { encryptPayload } from '@aions/crypto';
import { bytesToBase64 } from '../utils/bytes';

/** P10 (ADR-0018) — campos de identificação voluntária vindos do wizard. */
export interface IdentidadeInput {
  readonly identificacaoVoluntaria: boolean;
  readonly nome: string;
  readonly cpf: string;
  readonly contato: string;
}

/** Bloco cifrado da identidade, pronto para ir no corpo do POST /api/denuncia. */
export interface IdentidadeBlock {
  readonly identidadeCiphertext: string;
  readonly identidadeNonce: string;
}

/**
 * P10 (ADR-0018) — constrói o BLOCO CIFRADO SEPARADO da identificação voluntária.
 *
 * Invariante (provado por teste `identidade-block.test.ts`): só produz bloco quando o
 * opt-in (`identificacaoVoluntaria`) está marcado. Sem opt-in → objeto vazio → o POST
 * de denúncia fica BYTE-IDÊNTICO ao anônimo de hoje (nada muda no fluxo existente).
 *
 * O bloco é cifrado sob a MESMA DEK do caso (AAD = caseId), mas **separado** do
 * `ciphertext` principal de propósito: a IA-Analista decifra só o principal, então
 * nunca vê a identidade (Gemini free-tier — ver R-P10 / analista-decrypt.ts). CPF é
 * opcional (omitido do JSON cifrado quando vazio); nome/contato são obrigatórios na UI
 * quando o opt-in está ligado.
 *
 * Extraído de IntakeWizard.onSubmit para tornar o invariante testável sem dirigir a
 * SPA inteira — a lógica de cifra/opt-in é a parte de segurança que importa provar.
 */
export async function buildIdentidadeBlock(
  data: IdentidadeInput,
  dekRaw: Uint8Array,
  caseId: string,
): Promise<IdentidadeBlock | Record<string, never>> {
  if (!data.identificacaoVoluntaria) return {};
  const identidadePlain = JSON.stringify({
    nome: data.nome.trim(),
    ...(data.cpf.trim() ? { cpf: data.cpf.trim() } : {}),
    contato: data.contato.trim(),
  });
  const enc = await encryptPayload(identidadePlain, dekRaw, caseId);
  return {
    identidadeCiphertext: bytesToBase64(enc.ciphertext),
    identidadeNonce: bytesToBase64(enc.nonce),
  };
}
